import React from 'react';
import { Button } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { CustomDialog, CustomDialogProps } from 'components/CustomDialog';
import { useFormik } from 'formik';
import { useMutation } from '@apollo/react-hooks';
import {
  EMAIL_MESSAGE_SEND
} from 'components/organization/fm/inviteServiceProviders/sidebar/queries/emailMessageSend';
import { ErrorSnackbar, useErrorSnackbar } from 'components/notifications/ErrorSnackbar';
import { EmailMessageSend, EmailMessageSendVariables } from 'api/types/EmailMessageSend';

const CKEditorField = React.lazy(() => import('../../../../../form/CKEditorField'));

const useStyles = makeStyles((theme) => createStyles({
  ckEditorField: {
    minWidth: 700,
    [theme.breakpoints.down('md')]: {
      minWidth: '100%',
    },
  },
}));

export interface EmailMessageModalProps extends Omit<
  CustomDialogProps,
  'id' | 'title' | 'actions' | 'size' | 'loading' | 'onClose'
> {
  organization?: { organization_id: number; name: string; };
  onClose: (success?: boolean) => void;
}

export const EmailMessageModal: React.FC<EmailMessageModalProps> = ({
  organization,
  ...dialogProps
}) => {
  const classes = useStyles();
  const [emailMessageSend, {
    loading,
    error,
  }] = useMutation<EmailMessageSend, EmailMessageSendVariables>(EMAIL_MESSAGE_SEND);
  const emailMessageSendErrorSnackbar = useErrorSnackbar(error);

  const form = useFormik({
    initialValues: { body: '' },
    onSubmit: async (values) => {
      if (!organization) {
        return;
      }

      const { errors } = await emailMessageSend({
        variables: { organizationId: organization.organization_id, body: values.body },
      });
      if (!errors?.length) {
        if (dialogProps?.onClose) {
          dialogProps?.onClose(true);
        }
        form.resetForm();
      }
    },
  });

  return (
    <>
      <CustomDialog
        {...dialogProps}
        id="email-message-modal"
        title="Send Email Message"
        loading={loading}
        maxWidth="xl"
        onClose={() => dialogProps.onClose && dialogProps.onClose()}
        actions={(
          <Button
            disabled={loading}
            onClick={() => form.submitForm()}
            color="primary"
            variant="contained"
          >
            Send Message
          </Button>
        )}
      >
        <CKEditorField
          disabled={loading}
          label="Message"
          className={classes.ckEditorField}
          value={form.values.body}
          onChange={(v) => form.setFieldValue('body', v)}
          error={!!form.errors.body}
          helperText={
            form.errors.body
              ? form.errors.body
              : 'NOTE: This text will be included into an email to the Service Provider'
          }
        />
      </CustomDialog>
      <ErrorSnackbar {...emailMessageSendErrorSnackbar}>
        Could not send email message.
      </ErrorSnackbar>
    </>
  );
};
